import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  afterNextRender,
  effect,
  inject,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { AppSvgIconComponent } from '@vallift/ngl';

interface EnterpriseStep {
  id: string;
  number: string;
  title: string;
  description: string;
}

@Component({
  selector: 'app-home-enterprise-section',
  templateUrl: './home-enterprise-section.component.html',
  styleUrl: './home-enterprise-section.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [RouterLink, AppSvgIconComponent],
})
export class HomeEnterpriseSectionComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly elementRef = inject(ElementRef);
  private autoAdvanceTimer: ReturnType<typeof setTimeout> | undefined;
  private hasEnteredView = false;

  private readonly loaderDurationMs = 6000;
  private readonly autoCycleStepIds = ['create', 'logic', 'workflow', 'deploy'] as const;

  readonly sectionInView = signal(false);

  readonly steps: EnterpriseStep[] = [
    {
      id: 'create',
      number: '01',
      title: 'Create the form',
      description:
        'Choose field types, layouts, and branding so every submission captures the right data for your team.',
    },
    {
      id: 'logic',
      number: '02',
      title: 'Configure logic',
      description:
        'Set validation rules, conditional branching, and approval paths around each response.',
    },
    {
      id: 'workflow',
      number: '03',
      title: 'Configure workflow',
      description:
        'Build custom workflows that send emails, trigger webhooks, fire alerts, and run connected actions when submissions arrive.',
    },
    {
      id: 'deploy',
      number: '04',
      title: 'Deploy and integrate',
      description:
        'Publish forms, connect integrations, and sync submissions to the tools your organization already uses.',
    },
    {
      id: 'insights',
      number: '05',
      title: 'Data insights',
      description:
        'Summarize submissions, detect trends, and surface actionable insights from your form data with AI-powered analytics.',
    },
  ];

  readonly activeStepId = signal('create');

  constructor() {
    effect(() => {
      const activeId = this.activeStepId();
      const inView = this.sectionInView();
      this.clearAutoAdvanceTimer();

      if (
        !inView ||
        !this.autoCycleStepIds.includes(activeId as (typeof this.autoCycleStepIds)[number])
      ) {
        return;
      }

      this.autoAdvanceTimer = setTimeout(() => {
        this.advanceAutoStep();
      }, this.loaderDurationMs);
    });

    afterNextRender(() => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          const inView = entry?.isIntersecting ?? false;
          this.sectionInView.set(inView);

          if (inView && !this.hasEnteredView) {
            this.hasEnteredView = true;
            this.activeStepId.set('create');
          }
        },
        { threshold: 0.25 },
      );

      observer.observe(this.elementRef.nativeElement);
      this.destroyRef.onDestroy(() => observer.disconnect());
    });

    this.destroyRef.onDestroy(() => {
      this.clearAutoAdvanceTimer();
    });
  }

  setActiveStep(id: string): void {
    this.activeStepId.set(id);
  }

  isActiveStep(id: string): boolean {
    return this.activeStepId() === id;
  }

  isAnimatingStep(id: string): boolean {
    return this.sectionInView() && this.isActiveStep(id);
  }

  private advanceAutoStep(): void {
    const currentIndex = this.autoCycleStepIds.indexOf(
      this.activeStepId() as (typeof this.autoCycleStepIds)[number],
    );

    if (currentIndex === -1) {
      return;
    }

    const nextIndex = (currentIndex + 1) % this.autoCycleStepIds.length;
    this.activeStepId.set(this.autoCycleStepIds[nextIndex]);
  }

  private clearAutoAdvanceTimer(): void {
    if (this.autoAdvanceTimer !== undefined) {
      clearTimeout(this.autoAdvanceTimer);
      this.autoAdvanceTimer = undefined;
    }
  }
}
